import {
  EntryQuestionnaireReqDtoType,
  entryQuestionnaireResDto,
  UserStatusChangeReqDto,
  markUserAsResDto,
  userDataResDto,
  emailVerificationMailResDto,
  pausedUsersResDto,
  usersSearchResultResDto,
  onboardingDataResDto,
  userBagSurveyStatusResDto,
  UserFilterQueryReqDto,
  MyDataReqDto,
  usersFilterResDto,
  myCohortResDto,
  setNachversandEligibilityResDto,
  retrievalsResDto,
  setRetrievalStatusResDto,
  switchBdm1SampleToVoucherResDto,
  participantBdmStateResDto,
  userQuestionnaireStatusResDto,
  questionnaireCheckResDto,
  type QuestionnaireCheckTargetDto,
} from '@web/common';
import { RetrievalStatus } from '@prisma/client';
import { authenticatedAPI } from '../AuthenticatedAPI';
import { z } from 'zod';

export const searchUser = async (searchText: string) => {
  return authenticatedAPI.get(
    `/api/users/search/${searchText}`,
    usersSearchResultResDto,
  );
};

export const filterUsers = async (query: UserFilterQueryReqDto) => {
  return authenticatedAPI.get(`/api/users/search`, usersFilterResDto, query);
};

export const getUserById = async (userId: string) => {
  return authenticatedAPI.get(`/api/users/${userId}`, onboardingDataResDto);
};

export const updateEntryQuestionnaire = async (
  userId: string,
  entryQuestionnaire: EntryQuestionnaireReqDtoType,
) => {
  return authenticatedAPI.put(
    `/api/users/${userId}/entry-questionnaire`,
    entryQuestionnaire,
    entryQuestionnaireResDto,
  );
};

export const getMe = async () => {
  return authenticatedAPI.get(`/api/users/me`, userDataResDto);
};

export const markUserAs = async (
  userId: string,
  data: UserStatusChangeReqDto,
) => {
  return authenticatedAPI.post(
    `/api/users/${userId}/status-change`,
    data,
    markUserAsResDto,
  );
};

export const requestEmailVerificationMail = async () => {
  return authenticatedAPI.get(
    `/api/users/me/request-email-verification`,
    emailVerificationMailResDto,
  );
};

export const pausedUsers = async () => {
  return authenticatedAPI.get(`/api/users/paused`, pausedUsersResDto);
};

export const getMyCohort = async () => {
  return authenticatedAPI.get(`/api/users/me/cohort`, myCohortResDto);
};

export const getBagSurveyStatus = async () => {
  return authenticatedAPI.get(
    `/api/users/me/bag-survey-status`,
    userBagSurveyStatusResDto,
  );
};

export const getQuestionnaireStatusForUser = async (userId: string) => {
  return authenticatedAPI.get(
    `/api/users/${userId}/questionnaire-status`,
    userQuestionnaireStatusResDto,
  );
};

export const checkQuestionnaireForUser = async (
  userId: string,
  target: QuestionnaireCheckTargetDto,
) => {
  return authenticatedAPI.post(
    `/api/users/${userId}/questionnaire-status/check`,
    target,
    questionnaireCheckResDto,
    // one Qualtrics check can take up to about 30s
    { timeout: 55_000 },
    // the row shows the failed check; a toast per row would repeat it for "check all"
    { notifyOnError: false },
  );
};

export const getBagSurveyStatusByHorizon = (
  response: Awaited<ReturnType<typeof getBagSurveyStatus>>,
  horizon: number,
) => {
  return response.horizons.find((h) => h.horizon === horizon) ?? null;
};

export const updateMyData = async (data: MyDataReqDto) => {
  return authenticatedAPI.patch(`/api/users/my-data`, data, z.string());
};

export const setNachversandEligibility = async (
  userId: string,
  eligible: boolean,
) => {
  return authenticatedAPI.put(
    `/api/users/${userId}/nachversand-eligibility`,
    { eligible },
    setNachversandEligibilityResDto,
  );
};

export const getRetrievalsForUser = async (userId: string) => {
  return authenticatedAPI.get(
    `/api/users/${userId}/retrievals`,
    retrievalsResDto,
  );
};

export const getBdmStateForUser = async (userId: string) => {
  return authenticatedAPI.get(
    `/api/participant/bdm-state/${userId}`,
    participantBdmStateResDto,
  );
};

export const setRetrievalStatus = async (
  userId: string,
  retrievalId: number,
  status: RetrievalStatus,
) => {
  return authenticatedAPI.post(
    `/api/users/${userId}/retrievals/${retrievalId}/status`,
    { status },
    setRetrievalStatusResDto,
  );
};

export const switchBdm1SampleToVoucher = async (userId: string) => {
  return authenticatedAPI.post(
    `/api/users/${userId}/retrievals/bdm1/switch-to-voucher`,
    {},
    switchBdm1SampleToVoucherResDto,
  );
};
